import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { API_PATHS } from '../config/api-paths';
import { UwServicingApi } from '../tokens/uw-servicing.token';
import {
  UwCloseRequest,
  UwClosureReadiness,
  UwExpenditureControlView,
  UwExpenditureOverrideRequest,
  UwResidualBlock,
  UwServicingListParams,
  UwServicingListRow,
  UwServicingView,
} from '../models/uw-servicing.model';

/**
 * REST-only {@link UwServicingApi} — epic EM-1447 Story 11 (EM-1527).
 *
 * `GET /services/embedded/api/v1/uw/servicing[…]`. No caching: unlike the rating scale, servicing
 * state is live money — a cached schedule or a cached overdue rung would show an officer a settlement
 * position that has already moved.
 *
 * Rules 07 / 10 — no mock fallback, no `Mock*` sibling. The bearer rides the `authInterceptor`.
 * Errors are passed through untouched so callers can apply `extractBackendErrorMessage` (Rule 09):
 * the 422 `RESIDUAL_NOT_APPLICABLE` / `RESIDUAL_NOT_DUE` details are the whole explanation the officer
 * needs, and swallowing them here would leave the page with nothing to say.
 */
@Injectable()
export class UwServicingRestService implements UwServicingApi {
  private readonly http = inject(HttpClient);

  list(params?: UwServicingListParams): Observable<UwServicingListRow[]> {
    let httpParams = new HttpParams();
    if (params?.status) {
      httpParams = httpParams.set('status', params.status);
    }
    if (params?.product) {
      httpParams = httpParams.set('product', params.product);
    }
    if (params?.overdueOnly) {
      httpParams = httpParams.set('overdueOnly', 'true');
    }
    return this.http.get<UwServicingListRow[]>(API_PATHS.UW_SERVICING, { params: httpParams });
  }

  get(loanReference: string): Observable<UwServicingView> {
    return this.http.get<UwServicingView>(`${API_PATHS.UW_SERVICING}/${encodeURIComponent(loanReference)}`);
  }

  releaseResidual(loanReference: string, idempotencyKey: string): Observable<UwResidualBlock> {
    // PRD §11.2 — the literal header. This surface is hand-written (unlike the frozen `TaskResource`),
    // so it can and does honour the contract; the backend keys its `IdempotencyStore` on the loan.
    const headers = new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
    return this.http.post<UwResidualBlock>(
      `${API_PATHS.UW_SERVICING}/${encodeURIComponent(loanReference)}/residual/release`,
      {},
      { headers },
    );
  }

  // ── Stage 24 — card expenditure control + case closure (epic EM-1447 Story 12) ─────────────

  getExpenditureControl(loanReference: string): Observable<UwExpenditureControlView> {
    return this.http.get<UwExpenditureControlView>(
      `${API_PATHS.UW_SERVICING}/${encodeURIComponent(loanReference)}/expenditure-control`,
    );
  }

  overrideExpenditureControl(
    loanReference: string,
    request: UwExpenditureOverrideRequest,
    idempotencyKey: string,
  ): Observable<UwExpenditureControlView> {
    return this.http.post<UwExpenditureControlView>(
      `${API_PATHS.UW_SERVICING}/${encodeURIComponent(loanReference)}/expenditure-control`,
      request,
      { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) },
    );
  }

  getClosureReadiness(loanReference: string): Observable<UwClosureReadiness> {
    return this.http.get<UwClosureReadiness>(`${API_PATHS.UW_SERVICING}/${encodeURIComponent(loanReference)}/closure`);
  }

  close(loanReference: string, request: UwCloseRequest, idempotencyKey: string): Observable<UwClosureReadiness> {
    return this.http.post<UwClosureReadiness>(
      `${API_PATHS.UW_SERVICING}/${encodeURIComponent(loanReference)}/close`,
      request,
      { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) },
    );
  }
}
